/**
 * DocumentListSDKWrapper - SDK Wrapper for Document List operations
 *
 * Provides backward-compatible interface for ListApiService using generated SDK.
 *
 * Maps legacy ListApiService methods to:
 * - document SDK (dashboard, export, upload, restart operations)
 * - clickhouse SDK (warehouse dashboard)
 * - celery-tasks-manager SDK (task counts)
 *
 * Migration: Phase 2, Tier 1 (Low Complexity)
 * Service: ListApiService (15 endpoints)
 * Created: December 11, 2025
 */

import { BaseSDKWrapper } from './BaseSDKWrapper';
import { getDocument } from '../document/document';
import { getClickhouse } from '../clickhouse/clickhouse';
import { getCeleryTasksManager } from '../celery-tasks-manager/celery-tasks-manager';
import { customAxiosInstance } from '../../mutator/custom-axios-instance';
import axios from 'axios';
import { getAuthorizationToken } from 'src/pages/APIs';
import { getOrgId } from 'src/utils/organization/org_id_helper';

export class DocumentListSDKWrapper extends BaseSDKWrapper {
  private documentSDK: any;
  private clickhouseSDK: any;
  private celerySDK: any;

  constructor() {
    super('DocumentListSDK');
    this.documentSDK = getDocument();
    this.clickhouseSDK = getClickhouse();
    this.celerySDK = getCeleryTasksManager();
  }

  /**
   * Get warehouse dashboard data (clickhouse)
   *
   * @param query - Query string for filtering
   * @returns Promise resolving to warehouse dashboard data
   */
  async getListFromWarehouse(query: string): Promise<any> {
    this.logOperation('getListFromWarehouse', { query });

    // Parse query string into body and params
    const urlParams = new URLSearchParams(query);
    const body: any = {};

    // Build request body from query params
    Array.from(urlParams.entries()).forEach(([key, value]) => {
      body[key] = value;
    });

    // Keep parsing in one place. The generated SDK method only accepts
    // query params as its first argument.

    return this.execute(
      () => this.clickhouseSDK.dashboardClickhouseDashboardGet(body),
      'getListFromWarehouse',
    );
  }

  /**
   * Fetch opened groups data (multiple parallel requests)
   *
   * This method makes multiple parallel requests to the warehouse dashboard.
   * Note: Not a direct SDK call - uses custom parallel execution.
   *
   * @param queryStrings - Array of query strings
   * @returns Promise resolving to array of responses
   */
  async fetchOpendGroupsData(queryStrings: string[]): Promise<any> {
    this.logOperation('fetchOpendGroupsData', { count: queryStrings.length });

    const token: string = await getAuthorizationToken();

    const requestOptions = {
      headers: {
        Authorization: token,
      },
    };

    const requests = queryStrings.map((query) => {
      const urlParams = new URLSearchParams(query);
      const body: any = {};

      Array.from(urlParams.entries()).forEach(([key, value]) => {
        body[key] = value;
      });

      // Use SDK method wrapped in axios for parallel execution
      return this.clickhouseSDK
        .dashboardClickhouseDashboardGet(body)
        .catch((error: any) => {
          console.error(
            '[DocumentListSDK.fetchOpendGroupsData] Request failed:',
            error,
          );
          return error;
        });
    });

    return await Promise.all(requests);
  }

  /**
   * Get dashboard list data
   *
   * @param query - Query string for filtering
   * @returns Promise resolving to dashboard data
   */
  async getList(query: string): Promise<any> {
    this.logOperation('getList', { query });

    // Parse query string into params (NOT body - this is a GET request)
    const urlParams = new URLSearchParams(query);
    const params: any = {};

    // Build request params from query string
    // Note: dashboardDocumentDashboardsGet is a GET request, so filters must go in params
    Array.from(urlParams.entries()).forEach(([key, value]) => {
      params[key] = value;
    });

    return this.execute(
      () => this.documentSDK.dashboardDocumentDashboardsGet(params),
      'getList',
    );
  }

  /**
   * Export dashboard data
   *
   * @param query - Query string for filtering
   * @param type - Export type (e.g., 'csv', 'excel')
   * @returns Promise resolving to blob data
   */
  async exportDashboardData(query: string, type: string): Promise<any> {
    this.logOperation('exportDashboardData', { query, type });

    // Parse query string into params
    const urlParams = new URLSearchParams(query);
    const params: any = {};

    for (const [key, value] of urlParams.entries()) {
      params[key] = value;
    }

    const token = await getAuthorizationToken();

    // SDK expects an object with { type: string }, not FormData
    const body = { type: type };

    return this.execute(
      () =>
        this.documentSDK.exportDashboardDataDocumentExportsDashboardPost(body, {
          ...params,
          responseType: 'blob',
          headers: { Authorization: token },
        }),
      'exportDashboardData',
    );
  }

  /**
   * Upload files for processing
   *
   * @param formData - FormData containing files and metadata
   * @param subOrgId - Optional sub-organization ID
   * @returns Promise resolving to upload result
   */
  async uploadFiles(formData: FormData, subOrgId = ''): Promise<any> {
    this.logOperation('uploadFiles', { hasSubOrgId: !!subOrgId });

    // Extract files from FormData - SDK expects { files: Blob[], org_id: string, ... }
    const files: Blob[] = [];
    const filesFromForm = formData.getAll('files');
    filesFromForm.forEach((file) => {
      if (file instanceof Blob) {
        files.push(file);
      }
    });

    // Build the body object expected by the SDK
    // Get org_id from FormData, or fall back to getOrgId() helper
    const body: any = {
      files: files,
      org_id: formData.get('org_id') || getOrgId() || '',
      source: formData.get('source') || 'doc2app',
    };

    // Add optional fields if present
    if (subOrgId) {
      body.sub_org_id = subOrgId;
    } else if (formData.has('sub_org_id')) {
      body.sub_org_id = formData.get('sub_org_id');
    }

    if (formData.has('priority')) {
      body.priority = Number(formData.get('priority'));
    }

    if (formData.has('metadata')) {
      body.metadata = formData.get('metadata');
    }

    if (formData.has('doc_type')) {
      body.doc_type = formData.get('doc_type');
    }

    if (formData.has('assign_to')) {
      body.assign_to = formData.get('assign_to');
    }

    return this.execute(
      () =>
        this.documentSDK.processDocumentsDocumentProcessDocumentsPost(body, {}),
      'uploadFiles',
    );
  }

  /**
   * Assign documents to user/group
   *
   * @param data - Assignment data (doc_ids, user_id, etc.)
   * @returns Promise resolving to assignment result
   */
  async assignDocuments(data: any): Promise<any> {
    this.logOperation('assignDocuments', { data });

    // SDK expects doc_ids as an array, not a comma-separated string
    // The DocAssignment.vue passes doc_ids as "id1,id2,id3"
    const doc_ids_array =
      typeof data.doc_ids === 'string'
        ? data.doc_ids.split(',').filter((id: string) => id.trim() !== '')
        : data.doc_ids || [];

    const body = {
      org_id: data.org_id || getOrgId() || '',
      doc_ids: doc_ids_array,
      assign_to_user: data.assign_to_user || undefined,
      assign_to_sub_org: data.assign_to_sub_org || undefined,
      assign_to_group: data.assign_to_group || undefined,
    };

    return this.execute(
      () => this.documentSDK.assignDocumentsDocumentAssignPost(body, {}),
      'assignDocuments',
    );
  }

  /**
   * Get queued tasks state
   *
   * @returns Promise resolving to queued tasks count
   */
  async getTasksState(): Promise<any> {
    this.logOperation('getTasksState');

    return this.execute(
      () =>
        this.celerySDK.actualQueuedTaskCountCeleryTasksManagerGetQueuedTasksCountGet(),
      'getTasksState',
    );
  }

  /**
   * Get pending tasks count
   *
   * @returns Promise resolving to pending tasks count
   */
  async getPendingTasksCount(): Promise<any> {
    this.logOperation('getPendingTasksCount');

    return this.execute(
      () =>
        this.celerySDK.getPendingTasksCountCeleryTasksManagerGetPendingTasksCountPost(
          {},
        ),
      'getPendingTasksCount',
    );
  }

  /**
   * Get pending DOCUMENTS count — DB-truth, replaces the misleading
   * queue-depth-only `getPendingTasksCount` for the user-facing
   * header counter.
   *
   * The sibling `getPendingTasksCount` measures Celery queue depth,
   * which drops to 0 the moment workers pull the tasks (even though
   * the documents are still in `running`/`validating`/etc.). This
   * call hits the DB-based count via the new
   * `/celery_tasks_manager/get_pending_documents_count` endpoint
   * (see doc2-api PR #9436).
   *
   * Direct customAxiosInstance call instead of the generated SDK
   * because the SDK was last regenerated against an older OpenAPI
   * version and doesn't know the new route. When the SDK is
   * regenerated, switch to the typed binding.
   *
   * @returns Promise resolving to {total, load} pending docs count
   */
  async getPendingDocumentsCount(): Promise<any> {
    this.logOperation('getPendingDocumentsCount');

    return this.execute(
      () =>
        customAxiosInstance<{ total: number; load: string }>({
          url: '/celery_tasks_manager/get_pending_documents_count',
          method: 'POST',
        }),
      'getPendingDocumentsCount',
    );
  }

  /**
   * Restart multiple documents
   *
   * @param doc_ids - Comma-separated document IDs
   * @param force_restart - Force restart even if processing
   * @returns Promise resolving to restart result
   */
  async restartDocuments(doc_ids: string, force_restart = false): Promise<any> {
    this.logOperation('restartDocuments', { doc_ids, force_restart });

    // SDK expects doc_ids as an array, not a comma-separated string
    const doc_ids_array = doc_ids
      .split(',')
      .map((id: string) => id.trim())
      .filter((id: string) => id !== '');

    const body = {
      doc_ids: doc_ids_array,
      org_id: getOrgId() || '',
    };

    return this.execute(
      () =>
        this.documentSDK.restartDocumentsDocumentRestartPost(body, {
          force_restart: force_restart.toString(),
        }),
      'restartDocuments',
    );
  }

  /**
   * Restart export for multiple documents
   *
   * @param doc_ids - Comma-separated document IDs
   * @param force_restart - Force restart even if processing
   * @returns Promise resolving to restart result
   */
  async restartExportDocuments(
    doc_ids: string,
    force_restart = false,
  ): Promise<any> {
    this.logOperation('restartExportDocuments', { doc_ids, force_restart });

    return this.execute(
      () =>
        this.documentSDK.restartExportDocumentsDocumentRestartExportDocumentsDocIdsGet(
          doc_ids,
          { force_restart: force_restart.toString() },
        ),
      'restartExportDocuments',
    );
  }

  /**
   * Restart export for single document
   *
   * @param doc_id - Document ID
   * @param force_restart - Force restart even if processing
   * @returns Promise resolving to restart result
   */
  async restartExport(
    doc_id: string,
    force_restart = false,
    restart_from_failed = false,
  ): Promise<any> {
    this.logOperation('restartExport', {
      doc_id,
      force_restart,
      restart_from_failed,
    });

    return this.execute(
      () =>
        this.documentSDK.restartExportDocumentRestartExportDocIdGet(doc_id, {
          force_restart: force_restart.toString(),
          restart_from_failed: restart_from_failed.toString(),
        }),
      'restartExport',
    );
  }

  /**
   * Restart single document
   *
   * @param docId - Document ID
   * @param force_restart - Force restart even if processing
   * @returns Promise resolving to restart result
   */
  async restartDocument(docId: string, force_restart = false): Promise<any> {
    this.logOperation('restartDocument', { docId, force_restart });

    return this.execute(
      () =>
        this.documentSDK.restartDocumentRestartDocIdGet(docId, {
          force_restart: force_restart.toString(),
        }),
      'restartDocument',
    );
  }

  /**
   * Upload files for validation (append to existing document)
   *
   * @param formData - FormData containing files
   * @returns Promise resolving to upload result
   */
  async uploadFilesForValidation(formData: FormData): Promise<any> {
    this.logOperation('uploadFilesForValidation');

    // Extract files from FormData - SDK expects { doc_id, org_id, files: Blob[] }
    const files: Blob[] = [];
    const filesFromForm = formData.getAll('files');
    filesFromForm.forEach((file) => {
      if (file instanceof Blob) {
        files.push(file);
      }
    });

    // Build the body object expected by the SDK
    // Get org_id from FormData, or fall back to getOrgId() helper
    const body: any = {
      doc_id: formData.get('doc_id') || '',
      org_id: formData.get('org_id') || getOrgId() || '',
      files: files,
    };

    return this.execute(
      () => this.documentSDK.appendFilesToDocumentDocumentAppendPost(body, {}),
      'uploadFilesForValidation',
    );
  }

  /**
   * Get duplicate invoices
   *
   * @param querystring - Query string for filtering
   * @returns Promise resolving to duplicate invoices
   */
  async getDuplicateInvoices(querystring: string): Promise<any> {
    this.logOperation('getDuplicateInvoices', { querystring });

    // Parse query string into params
    const urlParams = new URLSearchParams(querystring);
    const params: any = {};

    Array.from(urlParams.entries()).forEach(([key, value]) => {
      params[key] = value;
    });

    return this.execute(
      () => this.documentSDK.findDuplicatesDocumentDuplicatesGet(params),
      'getDuplicateInvoices',
    );
  }
}

// Export singleton instance
export const documentListSDK = new DocumentListSDKWrapper();
